import { Annotation, Comment, User } from '@readcloud/data';
import React from 'react';

import {
  annotationsActions,
  applicationActions,
  authSelectors,
  commentsActions,
  commentsSelectors,
  readerActions,
  readerSelectors,
  useAppDispatch,
  usersSelectors,
} from '@readcloud/state';
import { ObjectId } from 'bson';
import { useSelector } from 'react-redux';
import { AnnotationItem } from './AnnotationItem';
import { useDeepCompareMemoize } from '../../../hooks/useDeepCompareMemoize';

interface Props {
  onClick?: (e: Annotation) => void;
  annotation: Annotation;
  setSelectedAnnotation?: (annotation: Annotation | undefined) => void;
  updated?: string;
  className?: string;
}

export const ConnectedAnnotationItem: React.FC<Props> = (props: Props) => {
  const user = useSelector(authSelectors.getUser);
  const users = useSelector(usersSelectors.getUsers);
  const comments = useSelector(commentsSelectors.getComments);

  const dispatch = useAppDispatch();

  const [authorMap, setAuthorMap] = React.useState<{ [id: string]: User }>({});
  const [newCommentText, setNewCommentText] = React.useState<string>('');

  React.useEffect(() => {
    if (users?.length) {
      const tempUsersMap = {};
      users.forEach((user) => (tempUsersMap[user.id] = user));
      setAuthorMap(tempUsersMap);
    }
  }, [useDeepCompareMemoize(users)]);

  const handleDeleteAnnotation = React.useCallback(
    (annotation: Annotation) => {
      dispatch(annotationsActions.asyncActions.deleteAnnotation(annotation.id));
      dispatch(annotationsActions.setSelectedAnnotation(''));
      props.setSelectedAnnotation && props.setSelectedAnnotation(undefined);
    },
    [dispatch, props.setSelectedAnnotation]
  );

  const handleEditAnnotation = React.useCallback(
    (annotation: Annotation) => {
      if (!annotation) {
        return;
      }
      dispatch(annotationsActions.setSelectedAnnotation(annotation.id));

      /* If is an overlay anno */
      if (
        annotation.type &&
        annotation.version &&
        annotation.version !== '2' &&
        annotation.type !== 'Text' &&
        annotation.location
      ) {
        dispatch(annotationsActions.updateCurrentTool('Select'));
        dispatch(
          readerActions.updateDocumentControl({
            type: 'changeLocation',
            payload: {
              location: annotation.location.pageNumber.toString(),
            },
          })
        );
        dispatch(
          annotationsActions.updateCurrentlyDrawing(
            annotation.location.pageNumber
          )
        );
        dispatch(annotationsActions.setSelectedAnnotation(annotation.id));
        dispatch(
          applicationActions.updateApplication({
            annotationsSidebarOpen: false,
          })
        );
      } else {
        dispatch(
          applicationActions.updateApplication({
            annotationUpdateModalOpen: true,
          })
        );
      }
    },
    [dispatch]
  );

  const onCommentTextChange = (e: React.FormEvent<HTMLInputElement>) => {
    setNewCommentText(e.currentTarget.value);
  };

  const submitComment = React.useCallback(() => {
    const comment: Comment = {
      authorId: user.id,
      clientCreated: new Date().toISOString(),
      clientUpdated: new Date().toISOString(),
      created: new Date().toISOString(),
      updated: new Date().toISOString(),
      deleted: false,
      id: new ObjectId().valueOf().toString(),
      subjectId: props.annotation?.id,
      subjectType: 'Annotation',
      version: '2',
      visibleInClouds: [],
      text: newCommentText,
    };
    dispatch(commentsActions.asyncActions.addComment(comment));
    setNewCommentText('');
  }, [newCommentText, props.annotation?.id, user?.id]);

  const annotationComments = React.useMemo(() => {
    return comments.filter(
      (comment) =>
        comment.subjectType === 'Annotation' &&
        comment.subjectId === props.annotation?.id
    );
  }, [useDeepCompareMemoize(comments), props.annotation?.id]);

  return user ? (
    <AnnotationItem
      key={props.annotation?.id}
      comments={annotationComments}
      onCommentTextChange={onCommentTextChange}
      onSubmitComment={submitComment}
      newCommentText={newCommentText}
      anno={props.annotation}
      onDeleteAnnotation={() =>
        props.annotation ? handleDeleteAnnotation(props.annotation) : undefined
      }
      onEditAnnotation={() =>
        props.annotation ? handleEditAnnotation(props.annotation) : undefined
      }
      onClick={props.onClick}
      authorMap={authorMap}
      user={user}
    />
  ) : null;
};
